/**
 * Error Recovery and Retry Utilities
 *
 * Provides enterprise-grade error classification, retry logic, and recovery mechanisms
 * for bulk operations and other async processes.
 */

/**
 * Error classification types for different retry strategies
 */
export enum ErrorType {
  NETWORK = 'network',           // Network connectivity issues
  TIMEOUT = 'timeout',           // Request timeout errors
  RATE_LIMIT = 'rate_limit',     // API rate limiting
  SERVER_ERROR = 'server_error', // 5xx server errors
  VALIDATION = 'validation',     // 4xx validation errors
  AUTH = 'auth',                 // Authentication/authorization errors
  CONFLICT = 'conflict',         // 409 conflict errors
  NOT_FOUND = 'not_found',       // 404 not found errors
  UNKNOWN = 'unknown'            // Unclassified errors
}

/**
 * Retry configuration for different error types
 */
export interface RetryConfig {
  maxAttempts: number;
  initialDelay: number;      // Initial delay in milliseconds
  maxDelay: number;          // Maximum delay in milliseconds
  backoffMultiplier: number; // Exponential backoff multiplier
  shouldRetry: boolean;      // Whether this error type should be retried
}

/**
 * Default retry configurations for different error types
 */
export const DEFAULT_RETRY_CONFIGS: Record<ErrorType, RetryConfig> = {
  [ErrorType.NETWORK]: {
    maxAttempts: 3,
    initialDelay: 1000,
    maxDelay: 10000,
    backoffMultiplier: 2,
    shouldRetry: true
  },
  [ErrorType.TIMEOUT]: {
    maxAttempts: 3,
    initialDelay: 2000,
    maxDelay: 15000,
    backoffMultiplier: 2,
    shouldRetry: true
  },
  [ErrorType.RATE_LIMIT]: {
    maxAttempts: 5,
    initialDelay: 5000,
    maxDelay: 30000,
    backoffMultiplier: 2,
    shouldRetry: true
  },
  [ErrorType.SERVER_ERROR]: {
    maxAttempts: 3,
    initialDelay: 2000,
    maxDelay: 12000,
    backoffMultiplier: 2,
    shouldRetry: true
  },
  [ErrorType.VALIDATION]: {
    maxAttempts: 1,
    initialDelay: 0,
    maxDelay: 0,
    backoffMultiplier: 1,
    shouldRetry: false
  },
  [ErrorType.AUTH]: {
    maxAttempts: 1,
    initialDelay: 0,
    maxDelay: 0,
    backoffMultiplier: 1,
    shouldRetry: false
  },
  [ErrorType.CONFLICT]: {
    maxAttempts: 2,
    initialDelay: 1000,
    maxDelay: 5000,
    backoffMultiplier: 2,
    shouldRetry: true
  },
  [ErrorType.NOT_FOUND]: {
    maxAttempts: 1,
    initialDelay: 0,
    maxDelay: 0,
    backoffMultiplier: 1,
    shouldRetry: false
  },
  [ErrorType.UNKNOWN]: {
    maxAttempts: 2,
    initialDelay: 2000,
    maxDelay: 8000,
    backoffMultiplier: 2,
    shouldRetry: true
  }
};

/**
 * Classified error information
 */
export interface ClassifiedError {
  type: ErrorType;
  message: string;
  userMessage: string;
  originalError: any;
  isRetryable: boolean;
  suggestedAction?: string;
}

/**
 * Retry attempt information
 */
export interface RetryAttempt {
  attemptNumber: number;
  totalAttempts: number;
  delay: number;
  errorType: ErrorType;
}

/**
 * Retry result information
 */
export interface RetryResult<T> {
  success: boolean;
  result?: T;
  error?: ClassifiedError;
  attempts: number;
  totalDelay: number;
}

/**
 * Classify an error to determine its type and retry strategy
 */
export function classifyError(error: any): ClassifiedError {
  let type = ErrorType.UNKNOWN;
  let userMessage = 'An unexpected error occurred. Please try again.';
  let suggestedAction: string | undefined;

  // Extract error information
  const status = error?.response?.status || error?.status;
  const message = error?.response?.data?.detail || error?.message || 'Unknown error';

  // Network and connectivity errors
  if (error?.code === 'NETWORK_ERROR' ||
      error?.message?.includes('Network Error') ||
      error?.message?.includes('fetch') ||
      !navigator.onLine) {
    type = ErrorType.NETWORK;
    userMessage = 'Network connection problem. Please check your internet connection.';
    suggestedAction = 'Check your internet connection and try again.';
  }

  // Timeout errors
  else if (error?.code === 'TIMEOUT' ||
           error?.message?.includes('timeout') ||
           error?.message?.includes('ECONNABORTED')) {
    type = ErrorType.TIMEOUT;
    userMessage = 'Request timed out. The server may be busy.';
    suggestedAction = 'Wait a moment and try again. If the problem persists, try refreshing the page.';
  }

  // HTTP status code classification
  else if (status) {
    switch (status) {
      case 400:
        type = ErrorType.VALIDATION;
        userMessage = 'Invalid request. Please check your input and try again.';
        suggestedAction = 'Review the selected items and ensure they are valid for this operation.';
        break;

      case 401:
        type = ErrorType.AUTH;
        userMessage = 'Authentication required. Please log in again.';
        suggestedAction = 'Please log out and log back in to refresh your session.';
        break;

      case 403:
        type = ErrorType.AUTH;
        userMessage = 'Access denied. You may not have permission for this operation.';
        suggestedAction = 'Contact your administrator if you believe you should have access.';
        break;

      case 404:
        type = ErrorType.NOT_FOUND;
        userMessage = 'The requested item was not found. It may have been moved or deleted.';
        suggestedAction = 'Refresh the page to see the current data and try again.';
        break;

      case 409:
        type = ErrorType.CONFLICT;
        userMessage = 'Conflict detected. The item may have been modified by another user.';
        suggestedAction = 'Refresh the page to see the latest changes and try again.';
        break;

      case 429:
        type = ErrorType.RATE_LIMIT;
        userMessage = 'Too many requests. Please wait a moment before trying again.';
        suggestedAction = 'Wait a few minutes before attempting this operation again.';
        break;

      case 500:
      case 502:
      case 503:
      case 504:
        type = ErrorType.SERVER_ERROR;
        userMessage = 'Server error occurred. This is usually temporary.';
        suggestedAction = 'Wait a moment and try again. If the problem persists, contact support.';
        break;

      default:
        if (status >= 400 && status < 500) {
          type = ErrorType.VALIDATION;
          userMessage = 'Request error. Please check your input.';
        } else if (status >= 500) {
          type = ErrorType.SERVER_ERROR;
          userMessage = 'Server error occurred. Please try again.';
        }
    }
  }

  // AbortController cancellation (not an error)
  else if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
    type = ErrorType.VALIDATION; // Don't retry cancelled operations
    userMessage = 'Operation was cancelled.';
    suggestedAction = 'Try the operation again if needed.';
  }

  const config = DEFAULT_RETRY_CONFIGS[type];

  return {
    type,
    message,
    userMessage,
    originalError: error,
    isRetryable: config.shouldRetry,
    suggestedAction
  };
}

/**
 * Calculate delay for exponential backoff
 */
export function calculateBackoffDelay(
  attempt: number,
  config: RetryConfig,
  jitter = true
): number {
  let delay = config.initialDelay * Math.pow(config.backoffMultiplier, attempt - 1);
  delay = Math.min(delay, config.maxDelay);

  // Add jitter to prevent thundering herd
  if (jitter) {
    delay = delay * (0.5 + Math.random() * 0.5);
  }

  return Math.floor(delay);
}

/**
 * Sleep for specified milliseconds
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Retry a function with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: {
    errorType?: ErrorType;
    customConfig?: Partial<RetryConfig>;
    onRetry?: (attempt: RetryAttempt) => void;
    signal?: AbortSignal;
  } = {}
): Promise<RetryResult<T>> {
  const errorType = options.errorType || ErrorType.UNKNOWN;
  const config = { ...DEFAULT_RETRY_CONFIGS[errorType], ...options.customConfig };

  let lastError: ClassifiedError | undefined;
  let totalDelay = 0;

  for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
    try {
      // Check for cancellation
      if (options.signal?.aborted) {
        throw new Error('Operation was aborted');
      }

      const result = await fn();
      return {
        success: true,
        result,
        attempts: attempt,
        totalDelay
      };
    } catch (error) {
      const classifiedError = classifyError(error);
      lastError = classifiedError;

      // Don't retry if not retryable or if this is the last attempt
      if (!classifiedError.isRetryable || attempt >= config.maxAttempts) {
        break;
      }

      // Calculate delay for next attempt
      const delay = calculateBackoffDelay(attempt, config);
      totalDelay += delay;

      // Notify about retry attempt
      if (options.onRetry) {
        options.onRetry({
          attemptNumber: attempt,
          totalAttempts: config.maxAttempts,
          delay,
          errorType: classifiedError.type
        });
      }

      // Wait before retrying
      await sleep(delay);

      // Check for cancellation after delay
      if (options.signal?.aborted) {
        throw new Error('Operation was aborted');
      }
    }
  }

  return {
    success: false,
    error: lastError,
    attempts: config.maxAttempts,
    totalDelay
  };
}

/**
 * Retry specific failed items from a bulk operation result
 */
export interface BulkRetryOptions {
  onProgress?: (completed: number, total: number, errors: any[]) => void;
  signal?: AbortSignal;
  customRetryConfig?: Partial<RetryConfig>;
}

export interface BulkRetryResult {
  success_count: number;
  failure_count: number;
  total_count: number;
  results: Array<{
    issue_hash: string;
    success: boolean;
    error_message?: string;
    attempts: number;
  }>;
  overall_success: boolean;
}

/**
 * Retry failed items from a bulk operation
 */
export async function retryFailedItems<T>(
  failedItems: Array<{ id: string; error?: string }>,
  retryFn: (item: { id: string; error?: string }) => Promise<void>,
  options: BulkRetryOptions = {}
): Promise<BulkRetryResult> {
  const results: BulkRetryResult['results'] = [];
  const errors: any[] = [];
  let successCount = 0;
  let failureCount = 0;

  for (let i = 0; i < failedItems.length; i++) {
    const item = failedItems[i];

    try {
      // Check for cancellation
      if (options.signal?.aborted) {
        throw new Error('Operation was aborted');
      }

      const retryResult = await retryWithBackoff(
        () => retryFn(item),
        {
          customConfig: options.customRetryConfig,
          signal: options.signal,
          onRetry: (attempt) => {
            console.log(`Retrying item ${item.id} (attempt ${attempt.attemptNumber}/${attempt.totalAttempts})`);
          }
        }
      );

      if (retryResult.success) {
        results.push({
          issue_hash: item.id,
          success: true,
          attempts: retryResult.attempts
        });
        successCount++;
      } else {
        results.push({
          issue_hash: item.id,
          success: false,
          error_message: retryResult.error?.userMessage || 'Retry failed',
          attempts: retryResult.attempts
        });
        failureCount++;
        errors.push({ issue_hash: item.id, error: retryResult.error?.userMessage });
      }

      // Report progress
      if (options.onProgress) {
        options.onProgress(i + 1, failedItems.length, errors);
      }

    } catch (error) {
      const classifiedError = classifyError(error);
      results.push({
        issue_hash: item.id,
        success: false,
        error_message: classifiedError.userMessage,
        attempts: 1
      });
      failureCount++;
      errors.push({ issue_hash: item.id, error: classifiedError.userMessage });

      // Report progress
      if (options.onProgress) {
        options.onProgress(i + 1, failedItems.length, errors);
      }
    }
  }

  return {
    success_count: successCount,
    failure_count: failureCount,
    total_count: failedItems.length,
    results,
    overall_success: failureCount === 0
  };
}

/**
 * Get user-friendly error message with suggested actions
 */
export function getErrorDisplayInfo(error: any): {
  title: string;
  message: string;
  action?: string;
  isRetryable: boolean;
} {
  const classified = classifyError(error);

  const titles: Record<ErrorType, string> = {
    [ErrorType.NETWORK]: 'Connection Problem',
    [ErrorType.TIMEOUT]: 'Request Timeout',
    [ErrorType.RATE_LIMIT]: 'Too Many Requests',
    [ErrorType.SERVER_ERROR]: 'Server Error',
    [ErrorType.VALIDATION]: 'Invalid Request',
    [ErrorType.AUTH]: 'Authentication Error',
    [ErrorType.CONFLICT]: 'Conflict Detected',
    [ErrorType.NOT_FOUND]: 'Not Found',
    [ErrorType.UNKNOWN]: 'Unexpected Error'
  };

  return {
    title: titles[classified.type],
    message: classified.userMessage,
    action: classified.suggestedAction,
    isRetryable: classified.isRetryable
  };
}

/**
 * Generate a unique operation ID for tracking retry operations
 */
export function generateOperationId(): string {
  return `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Persist retry state to localStorage for recovery across page reloads
 */
export interface PersistedRetryState {
  operationId: string;
  type: 'bulk_resolve' | 'bulk_dismiss';
  entityId: string;
  failedItems: Array<{ id: string; error?: string }>;
  timestamp: number;
  originalTotal: number;
  successCount: number;
}

const RETRY_STATE_KEY = 'drcr_retry_state';
const RETRY_STATE_TTL = 30 * 60 * 1000; // 30 minutes

export function persistRetryState(state: PersistedRetryState): void {
  try {
    localStorage.setItem(RETRY_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to persist retry state:', error);
  }
}

export function getPersistedRetryState(): PersistedRetryState | null {
  try {
    const stored = localStorage.getItem(RETRY_STATE_KEY);
    if (!stored) return null;

    const state = JSON.parse(stored) as PersistedRetryState;

    // Check if state is expired
    if (Date.now() - state.timestamp > RETRY_STATE_TTL) {
      clearPersistedRetryState();
      return null;
    }

    return state;
  } catch (error) {
    console.warn('Failed to retrieve retry state:', error);
    return null;
  }
}

export function clearPersistedRetryState(): void {
  try {
    localStorage.removeItem(RETRY_STATE_KEY);
  } catch (error) {
    console.warn('Failed to clear retry state:', error);
  }
}